import React from 'react';
import {BrowserRouter as Router, Routes, Route} from 'react-router-dom';
import {HelmetProvider} from 'react-helmet-async';
import {Toaster} from './components/ui/toaster';
import {AuthProvider} from './contexts/PhpAuthContext';
import {HospitalAuthProvider} from './contexts/HospitalAuthContext';
import {HospitalBrandingProvider} from './contexts/HospitalBrandingContext';
import {DataProvider} from './contexts/DataContext';
// Dental Hub (Platform-level) pages
import LandingPage from './pages/dentalhub/LandingPage';
import RegisterHospitalPage from './pages/dentalhub/RegisterHospitalPage';
import DentalHubAdminLoginPage from './pages/dentalhub/DentalHubAdminLoginPage';
import DentalHubAdminRegisterPage from './pages/dentalhub/DentalHubAdminRegisterPage';
import DentalHubAdminForgotPasswordPage from './pages/dentalhub/DentalHubAdminForgotPasswordPage';
import DentalHubAdminResetPasswordPage from './pages/dentalhub/DentalHubAdminResetPasswordPage';
import DentalHubAdminDashboard from './pages/dentalhub/DentalHubAdminDashboard';
import PricingPage from './pages/dentalhub/PricingPage';

// Hospital (Tenant-level) pages
import HospitalLandingPage from './pages/hospitals/HospitalLandingPage';
import HospitalLoginPage from './pages/hospitals/HospitalLoginPage';
import AdminDashboard from './pages/hospitals/AdminDashboard';
import DoctorDashboard from './pages/hospitals/DoctorDashboard';
import ClinicLoginDashboard from './pages/hospitals/ClinicLoginDashboard';
import PatientDashboard from './pages/hospitals/PatientDashboard';
import PatientForm from './pages/hospitals/PatientForm';
import PatientLoginPage from './pages/hospitals/PatientLoginPage';
import LoginPage from './pages/hospitals/LoginPage';
import AdminLoginPage from './pages/hospitals/AdminLoginPage';
import RegisterPage from './pages/hospitals/RegisterPage';
import TrialExpiredPage from './pages/hospitals/TrialExpiredPage';
import ProtectedRoute from './components/ProtectedRoute';
import HospitalRouteGuard from './components/HospitalRouteGuard';
import PWAInstallPrompt from './components/ui/PWAInstallPrompt';

function App () {
  return (
    <HelmetProvider>
      <AuthProvider>
        <HospitalAuthProvider>
          <HospitalBrandingProvider>
            <DataProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                <div className="min-h-screen">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/patient/login" element={<PatientLoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/register/hospital" element={<RegisterHospitalPage />} />
              <Route path="/hospital/login" element={<HospitalLoginPage />} />
              <Route path="/admin/hub/login" element={<DentalHubAdminLoginPage />} />
              <Route path="/admin/hub/register" element={<DentalHubAdminRegisterPage />} />
              <Route path="/admin/hub/forgot-password" element={<DentalHubAdminForgotPasswordPage />} />
              <Route path="/admin/hub/reset-password" element={<DentalHubAdminResetPasswordPage />} />
              <Route path="/admin/hub/dashboard" element={<DentalHubAdminDashboard />} />
              <Route path="/pricing" element={<PricingPage />} />
              
              {/* Hospital-specific routes with slug - must be after all specific routes */}
              <Route 
                path="/:hospitalSlug" 
                element={<HospitalLandingPage />} 
              />
              <Route path="/:hospitalSlug/login" element={<HospitalLoginPage />} />
              <Route path="/:hospitalSlug/patient/login" element={<PatientLoginPage />} />
              {/* Slug-based patient dashboard route */}
              <Route
                path="/:hospitalSlug/patient/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <PatientDashboard />
                  </ProtectedRoute>
                }
              />
              {/* Legacy patient dashboard route (without slug) - keep for backward compatibility */}
              <Route
                path="/patient/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <PatientDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/form"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <PatientForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/dashboard"
                element={
                  <HospitalRouteGuard>
                    <ProtectedRoute allowedRoles={['doctor', 'chief_doctor']}>
                      <DoctorDashboard />
                    </ProtectedRoute>
                  </HospitalRouteGuard>
                }
              />
              {/* Slug-based hospital routes */}
              <Route
                path="/:hospitalSlug/admin/dashboard"
                element={
                  <HospitalRouteGuard>
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  </HospitalRouteGuard>
                }
              />
              <Route
                path="/:hospitalSlug/doctor/dashboard"
                element={
                  <HospitalRouteGuard>
                    <ProtectedRoute allowedRoles={['doctor', 'chief_doctor']}>
                      <DoctorDashboard />
                    </ProtectedRoute>
                  </HospitalRouteGuard>
                }
              />
              <Route
                path="/:hospitalSlug/clinic/dashboard"
                element={
                  <HospitalRouteGuard>
                    <ProtectedRoute allowedRoles={['clinic_login']}>
                      <ClinicLoginDashboard />
                    </ProtectedRoute>
                  </HospitalRouteGuard>
                }
              />
              {/* Legacy routes (without slug) - keep for backward compatibility */}
              <Route
                path="/admin/dashboard"
                element={
                  <HospitalRouteGuard>
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  </HospitalRouteGuard>
                }
              />
              <Route
                path="/clinic/dashboard"
                element={
                  <HospitalRouteGuard>
                    <ProtectedRoute allowedRoles={['clinic_login']}>
                      <ClinicLoginDashboard />
                    </ProtectedRoute>
                  </HospitalRouteGuard>
                }
              />
            </Routes>
                  <Toaster />
                  <PWAInstallPrompt />
                </div>
              </Router>
            </DataProvider>
          </HospitalBrandingProvider>
        </HospitalAuthProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
